import { useSnackbar } from 'notistack';
import { useApi } from '../../shared/context';
import { DataRoomDiligenceRequest } from '../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { QueryKeys, useShowAllDocumentsQuery } from '../../shared/hooks';
import { CerebroDrawer, CerebroForm, CerebroHeading, CerebroPrimaryButton, CerebroText } from '../../shared/components';
import { FormikUtils } from '../../shared/utils';
import { Field, Formik, FormikErrors } from 'formik';
import { getApiResponseData } from '../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import React from 'react';

interface AssociateDocumentsDrawerProps {
	dataRoomGuid: string | undefined;
	request: DataRoomDiligenceRequest | null;
	close: () => void;
}

export const AssociateDocumentsDrawer: React.FC<AssociateDocumentsDrawerProps> = ({ dataRoomGuid, request, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const allQuery = useShowAllDocumentsQuery(dataRoomGuid, {
		enabled: request != null,
		retry: false,
	});

	const isDocumentChecked = (reason: number, selections: number[] | undefined): boolean => {
		if (!selections) {
			return false;
		}
		return selections.filter(num => num == reason).length > 0;
	};

	const associateMutation = useMutation(
		async ({ values }: AssociateDocumentsFormMutationVariables) => {
			const { data, error } = await getApiResponseData<DataRoomDiligenceRequest>(
				api.post(
					CerebroApiEndpoints.DataRoomDiligenceRequests.AssociateDocuments(dataRoomGuid, request?.guid),
					values
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('Diligence request was updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={request != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				Associate Documents
			</CerebroHeading>
			<CerebroText>
				Select one or more existing documents in the document repository to associate to this diligence request.
				Associating a document will complete the request.
			</CerebroText>

			<Formik
				initialValues={{
					documents: [] as number[],
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(associateMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<TableContainer>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell sx={{ width: 30 }}>&nbsp;</TableCell>
												<TableCell>Title</TableCell>
												<TableCell>Folder Path</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{allQuery.isSuccess && (
												<>
													{allQuery.data.map(doc => (
														<TableRow key={doc.guid}>
															<TableCell sx={{ width: 30 }}>
																<Field
																	type="checkbox"
																	name="documents"
																	value={doc.id}
																	checked={isDocumentChecked(
																		doc.id,
																		values.documents
																	)}
																	onChange={(e: any) => {
																		const value = e.target.value;
																		let documents = values.documents ?? [];
																		if (e.target.checked) {
																			documents.push(parseInt(value));
																		} else {
																			documents = documents.filter(
																				num => num != parseInt(value)
																			);
																		}

																		setFieldValue('documents', documents);
																	}}
																/>
															</TableCell>
															<TableCell>{doc.name}</TableCell>
															<TableCell>{doc.folderPath}</TableCell>
														</TableRow>
													))}
												</>
											)}
											{allQuery.isLoading && (
												<>
													<TableRow>
														<TableCell sx={{ width: 30 }}>
															<Skeleton height={60} />
														</TableCell>
														<TableCell>
															<Skeleton height={60} />
														</TableCell>
														<TableCell>
															<Skeleton height={60} />
														</TableCell>
													</TableRow>
													<TableRow>
														<TableCell sx={{ width: 30 }}>
															<Skeleton height={60} />
														</TableCell>
														<TableCell>
															<Skeleton height={60} />
														</TableCell>

														<TableCell>
															<Skeleton height={60} />
														</TableCell>
													</TableRow>
												</>
											)}
										</TableBody>
									</Table>
								</TableContainer>
								{allQuery.isSuccess && (
									<>
										{allQuery.data.map(doc => (
											<React.Fragment key={doc.guid}>
												<Grid item xs={1}></Grid>
												<Grid item xs={11}></Grid>
											</React.Fragment>
										))}
									</>
								)}
								{allQuery.isLoading && (
									<Grid item xs={12}>
										<Skeleton height={60} />
										<Skeleton height={60} />
										<Skeleton height={60} />
									</Grid>
								)}
								{FormikUtils.IsInvalid('documents', errors, touched) && (
									<Grid item xs={12}>
										<CerebroText variant="error">{errors.documents}</CerebroText>
									</Grid>
								)}
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AssociateDocumentsFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AssociateDocumentsValues>;
type AssociateDocumentsValueErrors = FormikErrors<AssociateDocumentsValues>;
interface AssociateDocumentsValues {
	documents: number[];
}

function validateForm(values: AssociateDocumentsValues) {
	const errors: AssociateDocumentsValueErrors = {};

	if (!values.documents || values.documents.length === 0) {
		errors.documents = 'Must select at least one document to continue.';
	}

	return errors;
}
